import React, { useRef, useState, useEffect, useCallback } from 'react';
import FuseAnimate from '@fuse/core/FuseAnimate';
import { makeStyles, Select, MenuItem, FormControl, CircularProgress, Button } from '@material-ui/core';
import FusePageSimple from '@fuse/core/FusePageSimple';
import './AdminDashboard.css';

import { useDispatch } from 'react-redux';
import { getdashboarddata } from 'app/services/dashboard/dashBoard';
import * as Actions from 'app/store/actions';
import StatCard from './StatCard';

const useStyles = makeStyles({
	layoutRoot: {},
	sidebar: {
		width: 330,
	},
});

const PERIOD_LABELS = {
	daily: 'Today',
	weekly: 'This week',
	monthly: 'This month',
	till_date: 'All time',
};

const REFETCH_THROTTLE_MS = 60000;

const PACKAGE_CARD_STYLES = [
	{ icon: 'assets/images/Included_package.png', valueColor: '#4DA0ED' },
	{ icon: 'assets/images/premium_package.png', valueColor: '#C0C0C0' },
	{ icon: 'assets/images/ultimate_package.png', valueColor: '#FFD700' },
];

const FALLBACK_PACKAGE_CARD_STYLE = { icon: 'assets/images/packages.png', valueColor: '#757575' };

function AdminDashboard() {
	const classes = useStyles();
	const pageLayout = useRef(null);
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [lastUpdated, setLastUpdated] = useState(null);

	const [filters, setFilters] = useState({ type: 'till_date' });
	const [dashBoard, setDashBoard] = useState({});

	const requestIdRef = useRef(0);
	const mountedRef = useRef(true);
	const lastFetchRef = useRef(0);

	const handleView = (e) => {
		const { value } = e.target;
		setFilters({ type: value });
	};

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const fetchDashboard = useCallback(
		(type) => {
			requestIdRef.current += 1;
			const requestId = requestIdRef.current;
			setLoading(true);
			setError(null);
			getdashboarddata(type)
				.then((res) => {
					if (!mountedRef.current || requestId !== requestIdRef.current) return;
					setDashBoard(res.data);
					setLastUpdated(new Date());
					setError(null);
				})
				.catch(() => {
					if (!mountedRef.current || requestId !== requestIdRef.current) return;
					setError('Failed to fetch data, please refresh');
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch data, please refresh',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					if (!mountedRef.current || requestId !== requestIdRef.current) return;
					setLoading(false);
					lastFetchRef.current = Date.now();
				});
		},
		[dispatch]
	);

	useEffect(() => {
		fetchDashboard(filters.type);
	}, [filters.type, fetchDashboard]);

	useEffect(() => {
		const refetchIfStale = () => {
			if (document.visibilityState !== 'visible') return;
			if (Date.now() - lastFetchRef.current < REFETCH_THROTTLE_MS) return;
			fetchDashboard(filters.type);
		};
		window.addEventListener('focus', refetchIfStale);
		document.addEventListener('visibilitychange', refetchIfStale);
		return () => {
			window.removeEventListener('focus', refetchIfStale);
			document.removeEventListener('visibilitychange', refetchIfStale);
		};
	}, [filters.type, fetchDashboard]);

	const periodLabel = PERIOD_LABELS[filters.type];

	return (
		<FusePageSimple
			classes={{
				root: classes.layoutRoot,
				sidebar: classes.sidebar,
			}}
			content={
				<div className="pl-72 pr-72 pt-52 pb-136">
					<FuseAnimate animation="transition.slideLeftIn" duration={600}>
						<>
							<div className="flex items-center flex-nowrap justify-between">
								<div className="">
									<h1 className="font-bold m-0" style={{ fontSize: '20px' }}>
										Welcome Admin!
									</h1>
									<h5 className="font-bold" style={{ color: '#6C757D', fontSize: '18px' }}>
										Dashboard
									</h5>
									{lastUpdated ? (
										<p className="last-updated">
											Last updated at {lastUpdated.toLocaleTimeString()}
										</p>
									) : null}
								</div>

								<div>
									<FormControl className="filter-class">
										<Select
											name="filter"
											onChange={handleView}
											value={filters.type}
											id="view"
											// className=''
											style={{ width: 150, gap: 10 }}
											startAdornment={
												<>
													{filters.type === 'daily' && (
														<img
															className="cursor-pointer"
															src="assets/images/day.png"
															alt="Day"
														/>
													)}
													{filters.type === 'weekly' && (
														<img
															className="cursor-pointer"
															src="assets/images/week.png"
															alt="Week"
														/>
													)}
													{filters.type === 'monthly' && (
														<img
															className="cursor-pointer"
															src="assets/images/month.png"
															alt="Month"
														/>
													)}
													{filters.type === 'till_date' && (
														<img
															className="cursor-pointer"
															src="assets/images/day.png"
															alt="Day"
														/>
													)}
												</>
											}
										>
											<MenuItem value="daily">
												<span id="day">Day</span>
											</MenuItem>
											<MenuItem value="weekly">
												<span id="week">Week</span>
											</MenuItem>
											<MenuItem value="monthly">
												<span id="month">Month</span>
											</MenuItem>
											<MenuItem value="till_date">
												<span id="month">Till Date</span>
											</MenuItem>
										</Select>
									</FormControl>
								</div>
							</div>
							<div className="flex flex-row middle-div mt-32">
								{loading ? (
									<div className="flex-grow">
										<CircularProgress className="m-auto justify-center items-center flex" />
									</div>
								) : error ? (
									<div className="error-panel flex-grow flex flex-col items-center justify-center">
										<p className="error-panel-message">{error}</p>
										<Button
											variant="contained"
											color="primary"
											onClick={() => fetchDashboard(filters.type)}
										>
											Retry
										</Button>
									</div>
								) : (
									<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16 md:gap-32 lg:gap-64 w-full">
										<StatCard
											icon="assets/images/Total-Schools.png"
											value={dashBoard?.schools?.toLocaleString()}
											label="Total Schools"
											valueColor="#6372f4"
											period={periodLabel}
										/>

										<StatCard
											icon="assets/images/Revenue.png"
											value={dashBoard?.revenue?.toLocaleString('en-US', {
												style: 'currency',
												currency: 'USD',
											})}
											label="Gross Billings"
											valueColor="#04C01C"
											period={periodLabel}
											subline={
												dashBoard?.po_share != null
													? `PO Share: ${dashBoard.po_share.toLocaleString('en-US', {
															style: 'currency',
															currency: 'USD',
														})}`
													: null
											}
										/>

										<StatCard
											icon="assets/images/Activities.png"
											value={dashBoard?.activities?.toLocaleString()}
											label="Total no of Activities"
											valueColor="#AA2FFF"
											period={periodLabel}
										/>

										{Object.keys(dashBoard.package_status || {}).map((pkg, index) => {
											const cardStyle = PACKAGE_CARD_STYLES[index] ?? FALLBACK_PACKAGE_CARD_STYLE;
											return (
												<StatCard
													key={pkg}
													icon={cardStyle.icon}
													value={dashBoard[pkg]?.toLocaleString()}
													label={pkg}
													valueColor={cardStyle.valueColor}
													period={periodLabel}
												/>
											);
										})}

										<StatCard
											icon="assets/images/Active.png"
											value={dashBoard?.active_subscribers?.toLocaleString()}
											label="Total no of Active Subscribers"
											valueColor="#E845F3"
											period={periodLabel}
										/>

										<StatCard
											icon="assets/images/Streaming.png"
											value={dashBoard?.live_streaming}
											label="% of Subscribers with Live Streaming"
											valueColor="#FD9300"
											isPercentage
											period={periodLabel}
										/>
									</div>
								)}
							</div>
						</>
					</FuseAnimate>
				</div>
			}
			innerScroll
			ref={pageLayout}
		/>
	);
}

export default AdminDashboard;
